import { generateColors } from "@mantine/colors-generator";
import { MantineProvider, MantineThemeColorsOverride } from "@mantine/core";
import { useCallback, useState } from "react";
import { OnlyChildrenProps } from "../../utils/misc";
import { cssVariablesResolver } from "./cssVariablesResolver";
import {
  defaultTheme,
  type ForcedColorScheme,
  ThemeContext,
  type ThemeProps,
  type UpdateColorProps,
} from "./useTheme";

export function ThemeProvider({ children }: OnlyChildrenProps) {
  const [theme, setTheme] = useState<ThemeProps>(defaultTheme);
  const [forcedColorScheme, setForcedColorScheme] =
    useState<ForcedColorScheme>();

  const updateColors = useCallback((args: UpdateColorProps[]) => {
    // Generate new color palettes from given hex
    const newColors: MantineThemeColorsOverride = args.reduce((prev, color) => {
      if (color.generateColorPalette) {
        const palette = generateColors(color.colorHex);
        return {
          ...prev,
          [color.colorName]: palette,
        };
      }
      return { ...prev };
    }, {});
    // Override some theme colors with the new palette if specified
    const overrideThemeProps = args.reduce((prev, curr) => {
      return {
        ...prev,
        ...(curr.overrideColorName && {
          [curr.overrideColorName]: curr.colorName,
        }),
      };
    }, {});

    setTheme((current) => ({
      ...current,
      colors: {
        ...current.colors,
        ...newColors,
      },
      ...overrideThemeProps,
    }));
  }, []);

  const resetTheme = useCallback(() => {
    setTheme((current) => ({
      ...current,
      primaryColor: defaultTheme.primaryColor,
      secondaryColor: defaultTheme.secondaryColor,
    }));
  }, []);

  return (
    <ThemeContext.Provider
      value={{ theme, updateColors, resetTheme, setForcedColorScheme }}
    >
      <MantineProvider
        theme={theme}
        cssVariablesResolver={cssVariablesResolver}
        defaultColorScheme="dark"
        forceColorScheme={forcedColorScheme}
      >
        {children}
      </MantineProvider>
    </ThemeContext.Provider>
  );
}
